
import React, { createContext, useState, useContext } from 'react';

export interface WebhookContextType {
  webhookUrl: string;
  setWebhookUrl: (url: string) => void;
  isWebhookValid: () => boolean;
  error: Error | null;
  isLoading: boolean;
  triggerWebhook: (data: any) => Promise<boolean>;
  transactionFeeWebhookUrl: string;
  setTransactionFeeWebhookUrl: (url: string) => void;
  isTransactionFeeNotificationsEnabled: boolean;
  setTransactionFeeNotificationsEnabled: (enabled: boolean) => void;
}

export const WebhookContext = createContext<WebhookContextType | undefined>(undefined);

interface WebhookProviderProps {
  children: React.ReactNode;
}

export const WebhookProvider: React.FC<WebhookProviderProps> = ({ children }) => {
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [transactionFeeWebhookUrl, setTransactionFeeWebhookUrl] = useState<string>('');
  const [isTransactionFeeNotificationsEnabled, setTransactionFeeNotificationsEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const isWebhookValid = () => {
    if (!webhookUrl) return false;
    try {
      new URL(webhookUrl);
      return true;
    } catch (e) {
      return false;
    }
  };

  const triggerWebhook = async (data: any): Promise<boolean> => {
    if (!isWebhookValid()) return false;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      setIsLoading(false);
      return response.ok;
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Unknown webhook error'));
      setIsLoading(false);
      return false;
    }
  };

  return (
    <WebhookContext.Provider
      value={{
        webhookUrl,
        setWebhookUrl,
        isWebhookValid,
        isLoading,
        error,
        triggerWebhook,
        transactionFeeWebhookUrl,
        setTransactionFeeWebhookUrl,
        isTransactionFeeNotificationsEnabled,
        setTransactionFeeNotificationsEnabled
      }}
    >
      {children}
    </WebhookContext.Provider>
  );
};

export const useWebhook = (): WebhookContextType => {
  const context = useContext(WebhookContext);
  if (context === undefined) {
    throw new Error('useWebhook must be used within a WebhookProvider');
  }
  return context;
};
